import conditionalRules from '../data/conditional_rules.json' with { type: "json" };

/**
 * Apply conditional logic rules based on safety question answers
 * @param {Object} safetyQuestions - Object with safety question answers (true/false)
 * @param {Object} baseData - Base data (risks, pbms, tools, etc.)
 * @returns {Object} - Auto-filled data (risks, pbms, permits, loto, etc.)
 */
export function applyConditionalLogic(safetyQuestions = {}, baseData = {}) {
    const result = {
        additionalRisks: [],
        additionalPbms: [],
        additionalTools: [],
        permits: [],
        lotoSteps: [],
        lotoRequired: false,
        hygieneCategories: [],
        werkplekinspectieRequired: false,
        liftingPlanType: null // 'routine' | 'complex' | null
    };

    // === LIFTING LOGIC (Smart Selector) ===
    if (safetyQuestions.gehesen) {
        if (safetyQuestions.hijsen_routine) {
            result.liftingPlanType = 'routine';
        } else {
            result.liftingPlanType = 'complex';
            // Add blocking requirement
            result.additionalRisks.push({
                Risico: 'Afwijkende/Complexe Hijsklus',
                Beheersmaatregel: 'STOP: Specifiek Hijsplan/TRA is VERPLICHT. Upload dit bij de vergunning.'
            });
        }
    }

    // === MANUAL LIFTING LOGIC ===
    if (safetyQuestions.handmatig_tillen) {
        result.additionalRisks.push({
            Risico: 'Fysieke belasting (Tillen >25kg)',
            Beheersmaatregel: 'Gebruik tilhulp of til met 2 personen. Pas juiste tiltechniek toe.'
        });
    }

    // Apply each rule
    conditionalRules.conditional_rules.forEach(rule => {
        const conditionMet = safetyQuestions[rule.condition] === rule.condition_value;

        if (conditionMet) {
            rule.actions.forEach(action => {
                switch (action.type) {
                    case 'set_field':
                        result[action.field] = action.value;
                        break;

                    case 'add_loto_step':
                        result.lotoSteps.push({
                            type: action.loto_type,
                            description: action.description
                        });
                        break;

                    case 'add_pbm':
                        if (!result.additionalPbms.includes(action.pbm)) {
                            result.additionalPbms.push(action.pbm);
                        }
                        break;

                    case 'add_risk':
                        result.additionalRisks.push({
                            Risico: action.risk,
                            Beheersmaatregel: action.measure
                        });
                        break;

                    case 'add_permit':
                        if (!result.permits.includes(action.permit)) {
                            result.permits.push(action.permit);
                        }
                        break;

                    case 'add_tool':
                        if (!result.additionalTools.includes(action.tool)) {
                            result.additionalTools.push(action.tool);
                        }
                        break;

                    case 'add_tool_suggestion':
                        if (!result.additionalTools.includes(action.tool)) {
                            result.additionalTools.push(action.tool + ' (aanbevolen)');
                        }
                        break;

                    case 'add_hygiene_category':
                        if (!result.hygieneCategories.includes(action.category)) {
                            result.hygieneCategories.push(action.category);
                        }
                        break;

                    default:
                        console.warn(`Unknown action type: ${action.type}`);
                }
            });
        }
    });

    // Safeguard: Ensure lotoRequired is true if we have LOTO steps
    if (result.lotoSteps.length > 0) {
        result.lotoRequired = true;
    }

    return result;
}

/**
 * Get location-specific risks based on selected location
 * @param {Object} data - Full data object
 * @param {string} hall - Selected hall
 * @param {string} line - Selected line
 * @returns {Array} - Array of location-specific risks
 */
export function getLocationRisks(data, hall, line) {
    const locationAttributes = data.locations_attributes || [];
    const risks = [];

    locationAttributes.forEach(attr => {
        const attrHall = attr.Hal;
        const attrLine = attr.Lijn;
        const attrRisk = attr.Risico;
        const attrMeasure = attr.Beheersmaatregel;

        // Match by hall and/or line
        if (attrRisk && attrMeasure) {
            if ((attrHall === hall || !attrHall) && (attrLine === line || !attrLine)) {
                risks.push({
                    Risico: attrRisk,
                    Beheersmaatregel: attrMeasure,
                    source: 'location'
                });
            }
        }
    });

    return risks;
}

/**
 * Get task-specific data (risks, steps, tools, pbms)
 * @param {Object} data - Full data object
 * @param {string} taskName - Selected task name
 * @returns {Object} - Task-specific data
 */
export function getTaskData(data, taskName) {
    const result = {
        risks: [],
        steps: [],
        tools: [],
        pbms: [],
        discipline: '',
        checks: []
    };

    if (!taskName) return result;

    // Get risks for this task (Legacy Array)
    (data.risks || []).forEach(risk => {
        if (risk['Specifieke Taak'] === taskName) {
            result.risks.push(risk);
        }
    });

    // Get steps for this task
    const taskMetadata = data.task_metadata?.[taskName];

    if (taskMetadata && taskMetadata.steps && Array.isArray(taskMetadata.steps) && taskMetadata.steps.length > 0) {
        // Use steps from metadata (new editor)
        result.steps = taskMetadata.steps
            .filter(s => s) // Filter nulls
            .map(s => ({
                "Stap Omschrijving": s.text || s.description || s["Stap Omschrijving"] || "Stap zonder tekst",
                "Opmerking (relatie tot parent)": ""
            }));
    } else {
        // Fallback to legacy steps
        (data.steps || []).forEach(step => {
            if (step['Specifieke Taak'] === taskName) {
                result.steps.push(step);
            }
        });
    }

    // Get tools for this task
    (data.tools || []).forEach(tool => {
        if (tool['Specifieke Taak'] === taskName) {
            result.tools.push(tool);
        }
    });

    // Get PBMs for this task
    (data.pbms || []).forEach(pbm => {
        if (pbm['Specifieke Taak'] === taskName) {
            result.pbms.push(pbm);
        }
    });

    // Get discipline and other metadata from task metadata
    if (taskMetadata) {
        result.discipline = taskMetadata['gekoppelde_Hoofddiscipline'] || '';
        result.qualification = taskMetadata['VP, VOP of Geen'] || 'Geen';

        // Extract Hazard Flags (Support both legacy flat keys and new nested object)
        const risksObj = taskMetadata["Risico's"] || {};

        result.hazards = {
            electrical: taskMetadata['Elektrisch?'] === 'Ja' || risksObj['Elektrisch'] === 'Ja',
            pressure: taskMetadata['Restdruk?'] === 'Ja' || risksObj['Restdruk'] === 'Ja',
            movement: taskMetadata['Onverwachtse beweging?'] === 'Ja' || risksObj['Onverwachtse beweging'] === 'Ja',
            heat: taskMetadata['Hoge temperatuur?'] === 'Ja' || risksObj['Hoge temperatuur'] === 'Ja',
            chemicals: taskMetadata['Gevaarlijke stoffen?'] === 'Ja' || risksObj['Gevaarlijke stoffen'] === 'Ja'
        };

        // Extract Specific LOTOTO
        result.specificLoto = [];
        const lotoTypes = ['Elektrisch', 'Pneumatisch', 'Hydraulisch', 'Thermisch', 'Zwaartekracht'];

        lotoTypes.forEach(type => {
            const instruction = taskMetadata[`LOTOTO ${type}`];
            const check = taskMetadata[`Check ${type}`];

            if (instruction || check) {
                result.specificLoto.push({
                    type: type,
                    instruction: instruction || '-',
                    check: check || '-'
                });
            }
        });

        // Also check for "LOTOTO Bron" if it's a general instruction
        if (taskMetadata['LOTOTO Bron']) {
            result.specificLoto.unshift({
                type: 'Bron / Algemeen',
                instruction: taskMetadata['LOTOTO Bron'],
                check: '-'
            });
        }

        // Extract Mechanic Checks
        if (taskMetadata.checks && Array.isArray(taskMetadata.checks)) {
            result.checks = taskMetadata.checks;
        }
    }

    // Auto-generate detailed risks if missing but flags are set
    if (result.hazards) {
        const hasRisk = (keyword) => result.risks.some(r => r.Risico && r.Risico.toLowerCase().includes(keyword.toLowerCase()));

        if (result.hazards.electrical && !hasRisk("Elektrische")) {
            result.risks.push({ Risico: "Elektrische Spanning", Beheersmaatregel: "Installatie spanningsloos maken, vergrendelen (LOTOTO) en nameten." });
        }
        if (result.hazards.pressure && !hasRisk("Restdruk")) {
            result.risks.push({ Risico: "Restdruk (Pneumatiek/Hydrauliek)", Beheersmaatregel: "Systeem drukloos maken, ontluchten en borgen." });
        }
        if (result.hazards.movement && !hasRisk("Beweging")) {
            result.risks.push({ Risico: "Onverwachte Beweging", Beheersmaatregel: "Bewegende delen blokkeren/fixeren en aandrijving uitschakelen." });
        }
        if (result.hazards.heat && !hasRisk("Temperatuur")) {
            result.risks.push({ Risico: "Hoge Temperatuur", Beheersmaatregel: "Laat installatie afkoelen en draag hittebestendige handschoenen/kleding." });
        }
        if (result.hazards.chemicals && !hasRisk("Gevaarlijke Stoffen")) {
            result.risks.push({ Risico: "Gevaarlijke Stoffen", Beheersmaatregel: "Raadpleeg veiligheidsinformatieblad (VIB) en draag voorgeschreven PBM's." });
        }
    }

    return result;
}

/**
 * Combine all data sources (task, location, conditional logic)
 * @param {Object} data - Full data object
 * @param {Object} formValues - Current form values
 * @returns {Object} - Combined data for summary
 */
export function getCombinedData(data, formValues) {
    const taskData = getTaskData(data, formValues.task);
    const locationRisks = getLocationRisks(data, formValues.hall, formValues.line);
    const conditionalData = applyConditionalLogic(formValues.safetyQuestions, data);

    // Combine risks (remove duplicates)
    const allRisks = [
        ...taskData.risks,
        // ...locationRisks,
        ...conditionalData.additionalRisks
    ];

    // Remove duplicate risks based on Risico text
    const uniqueRisks = (allRisks || []).filter((risk, index, self) =>
        risk && index === self.findIndex(r => r && r.Risico === risk.Risico)
    );

    // Combine PBMs (remove duplicates)
    // USER REQUEST: Disable "Smart Database" - Only show PBMs from Task Editor + Manual Step 3 Selection
    // Note: Step 3 selections are merged into 'formValues' tools/pbms by the Wizard component logic before calling this?
    // Wait, getCombinedData is called with formValues. formValues.task is just the name.
    // formValues.tools/pbms contains the current Step 3 state?
    // Let's check Wizard.jsx. Yes, StepComponent updates formData.
    // So 'taskData.pbms' is the BASE. 'formValues.pbms' IS the current selection (which started as Base).
    // So if ToolsPPEStep is working, we typically rely on formValues.pbms being the source of truth for Tools/PPEs.
    // BUT getCombinedData is calculating "extra" stuff.
    // We should just relying on what is passed to SummaryStep?
    // SummaryStep uses {...value, ...combinedData}.
    // If we simply STOP adding things here, combinedData won't have the extras.

    // However, for Risks, they are NOT in formValues (user can't edit risks in Wizard).
    // So for Risks, we utilize taskData.risks only.

    const allPbms = [
        ...taskData.pbms.map(p => p.PBM),
        // ...conditionalData.additionalPbms
    ];

    // Mandatory PBMs for Doetinchem (always required if location is selected)
    // USER REQUEST: Disable "invisible" location logic
    // if (formValues.location === 'Doetinchem') {
    //     allPbms.push("Veiligheidsschoenen");
    //     allPbms.push("Gehoorbescherming");
    // }
    const uniquePbms = [...new Set(allPbms)].map(pbm => ({ PBM: pbm }));

    // Combine tools (remove duplicates)
    const allTools = [
        ...taskData.tools.map(t => t.Tool),
        // ...conditionalData.additionalTools
    ];
    const uniqueTools = [...new Set(allTools)].map(tool => ({ Tool: tool }));

    // Calculate Risk Level
    let calculatedRiskLevel = 1;
    const qual = (taskData.qualification || '').trim().toUpperCase();
    if (qual === 'VP' || qual.includes('VP')) calculatedRiskLevel = 3;
    else if (qual === 'VOP' || qual.includes('VOP')) calculatedRiskLevel = 2;

    return {
        riskLevel: calculatedRiskLevel,
        risks: uniqueRisks,
        // For Steps, we just use taskData
        steps: taskData.steps,
        // For Tools/PPEs, SummaryStep actually merges formValues.tools with combinedData.tools.
        // By stripping extras here, SummaryStep will only see duplicates of taskData (which formValues already has) or nothing new.
        // This effectively disables the smart additions.
        pbms: uniquePbms,
        tools: uniqueTools,
        discipline: taskData.discipline,
        qualification: taskData.qualification,
        hazards: {
            electrical: taskData.hazards?.electrical || formValues.safetyQuestions?.elektrische_spanning || false,
            pressure: taskData.hazards?.pressure || formValues.safetyQuestions?.restdruk || false,
            movement: taskData.hazards?.movement || formValues.safetyQuestions?.onverwachte_beweging || false,
            heat: taskData.hazards?.heat || formValues.safetyQuestions?.gevaarlijke_temperaturen || false,
            chemicals: taskData.hazards?.chemicals || formValues.safetyQuestions?.contact_processtoffen || false
        },
        specificLoto: taskData.specificLoto,
        permits: conditionalData.permits,
        lotoSteps: conditionalData.lotoSteps,
        lotoRequired: conditionalData.lotoRequired,
        hygieneCategories: conditionalData.hygieneCategories,
        werkplekinspectieRequired: conditionalData.werkplekinspectieRequired,
        liftingPlanType: conditionalData.liftingPlanType,
        checks: taskData.checks
    };
}
